import { last } from 'lodash-es';
import type { Collection } from '@/hooks/collections/use-collections';
import { ConversationMessageContentType, type ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import { getSafeMediaUrl } from './media-url';
import { canParseAsUrl, parseUrl } from './parse-url';

export const getFileBySource = async (src: string, fileName?: string) => {
  if (!src || !canParseAsUrl(src)) return;

  const safeSrc = getSafeMediaUrl(src);

  const blob = await fetch(safeSrc, { mode: 'cors' }).then((res) => {
    return res.blob();
  });

  const extension = blob.type.split('/').pop();
  const fallbackFilename = parseUrl(src)?.pathname?.split('/').pop() || `file.${extension}`;

  // Android cannot recognize GIF with correct MIME type
  if (blob.type === 'application/octet-stream' && (fileName?.includes('gif') || fallbackFilename?.includes('gif'))) {
    return new File([blob], fileName || fallbackFilename, {
      type: 'image/gif',
    });
  }

  const file = new File([blob], fileName?.includes('.') ? fileName : `${fileName}.${extension}` || fallbackFilename, {
    type: blob.type,
  });

  return file;
};

export const getCollectionFileName = (collection: Collection) => {
  const unknown = 'unknown-collection';
  switch (collection.Type) {
    case ConversationMessageContentType.Image:
    case ConversationMessageContentType.Audio:
    case ConversationMessageContentType.Video: {
      const src = collection.Content;
      return parseUrl(src)?.pathname?.split('/').pop() ?? unknown;
    }
    case ConversationMessageContentType.File: {
      const [filename = unknown] = collection.Content.split('|');
      return filename;
    }
    default:
      return unknown;
  }
};
export const getMessageFileName = (message: Pick<ConversationMessageTypeUnion, 'Message' | 'MsgType'>) => {
  const unknown = 'unknown-message';
  switch (message.MsgType) {
    case ConversationMessageContentType.Image:
    case ConversationMessageContentType.Audio:
    case ConversationMessageContentType.Video: {
      const src = message.Message;
      return parseUrl(src)?.pathname?.split('/').pop() ?? unknown;
    }
    case ConversationMessageContentType.File: {
      const [filename = unknown] = message.Message.split('|');
      return filename;
    }
    default:
      return unknown;
  }
};
